import * as ss from 'superstruct';
import type { AdAccountPlatform } from '../../../ad-platforms/ad-account-platform';
import { OutputStreamDestinationConfig } from '../types';
import { ConversionOutputStreamDestination } from './destination-list';
import { OpenAiAdsOutputStreamDestination } from './openai-ads-output-stream';

type DestinationConfig = {
  /** Write-only fields, masked on read. */
  sensitiveFields: string[];
  /** The platform of the ad account its streams send to, if any. */
  adAccount?: AdAccountPlatform;
  /**
   * Checked whole when a stream is saved: its consumer rejects an invalid
   * config, which would otherwise block every conversion after it's saved.
   */
  struct?: ss.Struct<any, any>;
};

/** Hardcoded config for each output stream destination. */
export const outputStreamDestinationConfig: Record<
  ConversionOutputStreamDestination,
  DestinationConfig
> = {
  email: { sensitiveFields: [] },
  webhook: { sensitiveFields: [] },
  'meta-ads': {
    sensitiveFields: ['accessToken'],
    adAccount: 'meta',
  },
  'google-ads-data-manager': {
    sensitiveFields: [],
    adAccount: 'google',
  },
  'microsoft-ads-conversion': {
    sensitiveFields: [],
    adAccount: 'bing',
  },
  'openai-ads': {
    sensitiveFields: ['apiKey'],
    adAccount: 'openai',
    struct: OpenAiAdsOutputStreamDestination,
  },
};

const configFor = (destinationName: string): DestinationConfig | undefined =>
  outputStreamDestinationConfig[
    destinationName as ConversionOutputStreamDestination
  ];

export function getSensitiveFieldsForDestination(
  destinationName: string,
): string[] {
  return configFor(destinationName)?.sensitiveFields ?? [];
}

/** The platform of the ad account streams to `destinationName` send to. */
export const outputStreamAdAccountPlatform = (
  destinationName: string,
): AdAccountPlatform | undefined => configFor(destinationName)?.adAccount;

/**
 * `destination`, checked whole if its config has a `struct`. Throws a
 * `StructError` with a user-facing message if it's invalid.
 *
 * The data provider validates writes against the generic
 * `OutputStreamDestinationConfig`, so this is how specific ones get checked.
 */
export const validateOutputStreamDestination = (
  destination: OutputStreamDestinationConfig,
): OutputStreamDestinationConfig => {
  const struct = configFor(destination.name)?.struct;
  return struct
    ? (ss.create(destination, struct) as OutputStreamDestinationConfig)
    : destination;
};
